import type { Dispatch } from 'redux';
import { createAction } from '@reduxjs/toolkit';
import { debounce } from 'ts-debounce';

import type { AccommodationState, Filters, SearchResultsState, ThunkResult } from '@/interfaces';
import { filtersToResultPageBrowserQueryParams } from '@/utils/url/resultPageQueryParams';
import { getHotels } from '@/utils/fetch/kayak/kayakFetcher';
import { actions as mapActions, filtersToCurrentFilters as filtersToCurrentMapFilters } from '@/state/mapActions';
import { updateSearchResultsPageQueryParams } from '@/utils/url/routerHelpers';

// Actions
// -------
export const resetFetchStatus = createAction('RESULT_LIST/RESET_FETCH_STATUS');

export const clearSearchResults = createAction<string>('RESULT_LIST/CLEAR');

export const setSearchResultsPending = createAction('RESULT_LIST/HOTELS_PENDING');

export const setSearchResults = createAction<{
    data: Partial<SearchResultsState>;
    queryParams: string;
}>('RESULT_LIST/HOTELS_OK');

export const setSearchResultsError = createAction<string>('RESULT_LIST/HOTELS_ERROR');

// Not actions
// -----------filtersToResultPageBrowserQueryParams
export const resetFetchState = (dispatch: Dispatch, filters: Filters, resetMap: boolean): void => {
    const queryParams = filtersToResultPageBrowserQueryParams(filters, false);
    dispatch(clearSearchResults(queryParams));
    dispatch(setSearchResultsPending());
    if (resetMap) {
        const mapQueryParams = filtersToCurrentMapFilters(filters);
        dispatch(mapActions.resetMapHotels(mapQueryParams));
    }
};

export const debouncedFetchHotels = debounce(async (filters: Filters, dispatch: Dispatch) => {
    const { status, data } = await getHotels(filters);
    const queryParams = filtersToResultPageBrowserQueryParams(filters, false);

    if (status === 200) {
        dispatch(setSearchResults({ data, queryParams }));
        return;
    }

    dispatch(setSearchResultsError(queryParams));
}, 300);

export const pollHotels =
    (): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        dispatch(setSearchResultsPending());
        debouncedFetchHotels(getState().filters, dispatch);
    };

export const fetchHotels =
    (resetMap = true): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        const filters = getState().filters;
        resetFetchState(dispatch, filters, resetMap);
        await updateSearchResultsPageQueryParams(getState().map.open, filters, getState().searchResults.searchConfig);
        debouncedFetchHotels(filters, dispatch);
    };
